import React from 'react'
import { ResponsiveContainer, TooltipProps } from 'recharts'
import { Card, H4 } from '@blueprintjs/core'
import { Flex, Button, IconButton, Dialog } from '@ui'
import { Column } from '../ReportPage'
import { prettyNumber } from '@ui/PrettyNumber'
import { getColorScale } from '../config'
import { IGroup } from '../types'
import Stacked from '@ui/charts/StackedBar'
import ChartTooltip from '@ui/charts/ChartTooltip'

const MAX_BARS = 6

interface IProps {
  chart: IGroup
  width?: number
  height?: number
  layout: 'vertical' | 'horizontal'
  config: any
  onClick?: (a: any) => void
}

export default function StackedBarWrapper({
  chart,
  width = 200,
  height = 200,
  config,
  onClick,
}: IProps) {
  const [showModal, setShowModal] = React.useState<boolean>(false)

  const data = formatStackChartData({
    columns: chart.columns,
    columnTypes: chart.columnTypes,
    values: chart.values,
    conf: config,
  })

  const colors = getColorScale(chart.name, true)

  const truncated = data.length > MAX_BARS
  const DATA = truncated ? data.slice(0, MAX_BARS) : data

  return (
    <Column>
      <Card style={{ height: '100%', width: '100%', position: 'relative' }}>
        <H4>{chart.name}</H4>
        {truncated && (
          <Flex
            align="center"
            style={{
              position: 'absolute',
              top: 45,
              left: 20,
              zIndex: 10,
            }}
          >
            <span style={{ marginRight: 8 }}>
              Top {MAX_BARS}/{data.length} shown
            </span>
            <Button
              small
              minimal
              blue
              text="Show All"
              style={{ height: 32, paddingTop: 5, paddingBottom: 5 }}
              onClick={() => setShowModal(true)}
            />
          </Flex>
        )}
        <ResponsiveContainer height={height} width="100%">
          <Stacked
            data={DATA}
            layout="vertical"
            width={width}
            height={height}
            xdata={{ groups: config.x, label: config.xLabel, key: config.x }}
            ydata={{ key: config.y }}
            tooltip={(props) => <CustomTooltip config={config} {...props} />}
            colors={colors}
            margin={{ top: 30, right: 10, bottom: 5 }}
            onClick={onClick}
          />
        </ResponsiveContainer>
        <Dialog
          isOpen={showModal}
          isCloseButtonShown={false}
          style={{
            width: 900,
            height: data.length * 40 + 100,
            position: 'relative',
          }}
          title={chart.name}
          toolbar={
            <div className="dialog-buttons">
              <IconButton
                minimal
                iconName="times"
                tooltip="Close"
                onClick={() => setShowModal(false)}
              />
            </div>
          }
          body={
            <div style={{ margin: 15 }}>
              <ResponsiveContainer height={data.length * 40} width="100%">
                <Stacked
                  data={data}
                  layout="vertical"
                  width={width}
                  height={data.length * 40}
                  xdata={{
                    groups: config.x,
                    label: config.xLabel,
                    key: config.x,
                  }}
                  ydata={{ key: config.y }}
                  tooltip={(props) => <CustomTooltip config={config} {...props} />}
                  colors={colors}
                />
              </ResponsiveContainer>
            </div>
          }
        ></Dialog>
      </Card>
    </Column>
  )
}

const CustomTooltip = ({ active, label, payload, config }: TooltipProps & { config: any }) => {
  if (!active || !payload) {
    return null
  }

  return (
    <ChartTooltip
      label={label}
      value={payload.map((p: any, i: number) => {
        let valueEl = null

        if (config.unit === '$') {
          valueEl = `$${prettyNumber({ value: p.value })}`
        } else {
          valueEl = `${prettyNumber({ value: p.value })}${config.unit ? config.unit : ''}`
        }

        return (
          <Flex align="center" key={i}>
            <div
              style={{
                backgroundColor: `${p.color}`,
                width: 12,
                height: 12,
                borderRadius: 2,
                marginRight: 8,
              }}
            />
            {p.name}: {valueEl}
          </Flex>
        )
      })}
    />
  )
}

const formatValue = (columnType: string, value: string) =>
  ['int', 'decimal', 'percent'].indexOf(columnType) >= 0 ? +value : value

const formatStackChartData = ({
  columns,
  columnTypes,
  values,
  conf,
}: {
  columns: string[]
  columnTypes: string[]
  values: any
  conf: any
}) =>
  values
    .map((entry: any) =>
      columns.reduce((result: any, c: any, i: any) => {
        if (columnTypes[i] === 'id') {
          return { ...result, id: entry[i] }
        } else if (conf.x.indexOf(c) >= 0 || c === conf.y) {
          return {
            ...result,
            [c]: formatValue(columnTypes[i], entry[i]),
          }
        } else {
          return result
        }
      }, {})
    )
    .sort((d1: any, d2: any) => {
      const sum = (d: any) => conf.x.reduce((result: any, k: any) => result + d[k], 0)
      return sum(d2) - sum(d1)
    })
